<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 :id="testName + '-title'">
        {{ testName }}
      </h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="connexin-test-grid"
      >
        <div class="pl-2 pr-2">
          <p>
            <a target="_blank" href="https://www.vcgs.org.au/tests/connexin/"
              >Non-syndromic hearing loss
            </a>
            is a partial or total loss of hearing that is not associated with
            other signs and symptoms. Non-syndromic hearing loss can be caused
            by alterations in over 90 genes however, the most commonly
            associated genes are GJB2 and GJB6 genes.
          </p>
          <p>
            The GJB2 and GJB6 genes provides instructions for making the
            connexin 26 and connexin 30 proteins respectively. Alterations in
            the GJB2 or GJB6 gene change their connexin proteins, which may
            affect the function or survival of cells that are needed for
            hearing.
          </p>
          <p>GPs and specialists can order this test.</p>

          <p>
            Please <a href="mailto:vcgs@vcgs.org.au">contact VCGS</a> for
            prenatal testing.
          </p>
        </div>
        <span class="text-title">Test type</span>
        <v-select
          v-model="testType"
          :items="testTypeOptions"
          item-text="key"
          item-value="value"
          :id="testId + '.testType'"
          :rules="ruleNotNull"
          outlined
          hide-details="auto"
        ></v-select>
        <v-alert
          v-if="
            testType === 'diagnosticTesting' ||
              testType === 'carrierTesting' ||
              testType === 'partnerScreening'
          "
          text
          type="warning"
          icon="mdi-cash-usd-outline"
          outlined
          class="pl-0"
        >
          {{
            testType === "diagnosticTesting"
              ? "Testing for symptomatic patients."
              : ""
          }}
          {{
            testType === "carrierTesting"
              ? "Testing for a known family variant."
              : ""
          }}
          {{
            testType === "partnerScreening"
              ? "Testing for asymptomatic partners, with no known family variant, whose partner is a known carrier."
              : ""
          }}
        </v-alert>
        <v-alert
          v-if="testType === 'diagnosticTesting'"
          text
          color="info"
          outlined
        >
          Please note: GJB6 gene analysis is only performed when a single
          variant in the GJB2 gene is identified, or if the family is
          consanguineous
        </v-alert>
        <v-row>
          <v-col
            class="mt-4"
            v-if="
              testType === 'carrierTesting' || testType === 'partnerScreening'
            "
          >
            <span class="text-title"
              >Family member previously tested at VCGS</span
            >
            <v-select
              v-model="previouslyTested"
              :items="yesNoOptions"
              item-text="key"
              item-value="value"
              :id="testId + '.familyPrevTested'"
              outlined
              hide-details="auto"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-if="
              (testType === 'carrierTesting' ||
                testType === 'partnerScreening') &&
                previouslyTested
            "
          >
            <span class="text-title">VCGS sample ID</span>
            <v-text-field
              v-model="posCtrlSampleId"
              :id="testId + '.posCtrlSampleId'"
              outlined
              hide-details="auto"
            ></v-text-field>
            <span class="text-hint"
              >e.g. 20C100123. This is located in the top right section of our
              reports.</span
            >
          </v-col>
        </v-row>
        <div
          class="mt-4"
          v-if="
            (testType === 'carrierTesting' ||
              testType === 'partnerScreening') &&
              previouslyTested
          "
        >
          <span class="text-title">Family member name</span>
          <v-text-field
            v-model="posCtrlName"
            :id="testId + '.posCtrlName'"
            outlined
            hide-details="auto"
          ></v-text-field>
        </div>
        <v-menu
          v-if="
            (testType === 'carrierTesting' ||
              testType === 'partnerScreening') &&
              previouslyTested
          "
          v-model="familyMemberDOBMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <div
              class="mt-4"
              v-if="
                testType === `carrierTesting` || testType === 'partnerScreening'
              "
            >
              <span class="text-title">Family member date of birth</span>

              <v-text-field
                v-model="familyMemberDOBDisplay"
                :id="testId + '.posCtrlBirthDate'"
                v-on="on"
                :rules="familyMemberDOBDisplay != '' ? ruleDate : []"
                outlined
                hide-details="auto"
              ></v-text-field>
            </div>
          </template>
          <v-date-picker
            locale="en-au"
            :min="minDate"
            :max="maxDate"
            v-model="familyDOBVal"
            @input="familyMemberDOBMenu = false"
          ></v-date-picker>
        </v-menu>

        <div
          class="mb-3 mt-3 mb-md-6 mt-md-8"
          v-if="
            this.$dataset.isUploadPreviousReportEnabled &&
              (testType === 'carrierTesting' ||
                testType === 'partnerScreening') &&
              previouslyTested === false
          "
        >
          <UploadComponent
            :testId="testId"
            :testName="testName"
            :updateOnDestroy="true"
            @updateUploadObj="updateUploadObj"
            uploadType="previousReport"
          />
        </div>
        <div
          class="mt-4"
          v-if="
            testType === `carrierTesting` || testType === 'partnerScreening'
          "
        >
          <span class="text-title">Relationship to person being tested*</span>
          <v-text-field
            v-model="rhipToPosCtrl"
            :id="testId + '.rhipToPosCtrl'"
            :rules="ruleNotNull"
            outlined
            hide-details="auto"
          ></v-text-field>
          <span class="text-hint">e.g. sibling, parent, aunt, etc.</span>
        </div>
        <div
          class="mt-4"
          v-if="
            testType === `carrierTesting` || testType === 'partnerScreening'
          "
        >
          <span class="text-title"
            >Is the person being tested affected by the same condition?*</span
          >
          <v-text-field
            v-model="relationAffected"
            :id="testId + '.relationAffected'"
            :rules="ruleNotNull"
            outlined
            hide-details="auto"
          />
        </div>
        <div class="mt-4 mb-4" v-if="testType === 'diagnosticTesting'">
          <span class="text-title">Is there parental consanguinity?</span>
          <v-select
            v-model="consanguinity"
            :items="yesNoOptions"
            item-text="key"
            item-value="value"
            :id="testId + '.consanguinity'"
            outlined
            hide-details="auto"
          />
        </div>
        <div class="mt-10">
          <span class="text-title">Clinical notes</span>
          <v-textarea
            v-model="clinicalNotes"
            :id="testId + '.clinicalNotes'"
            outlined
            hide-details="auto"
          ></v-textarea>
          <span class="text-hint"
            >e.g. clinical indication for testing, relevant family history,
            results of relevant prior genetic and non-genetic investigations,
            biochemistry, histopathology and imaging, etc.</span
          >
        </div>
        <TestPriceAlert :price="price" :medicare="medicare">
          <template v-slot:funding>
            <p v-if="testType === 'diagnosticTesting'" class="pt-3">
              State funded for victorian patients.
            </p>
            <p v-if="testType === 'carrierTesting'" class="pt-3">
              State funding available for Victorian parents of an affected child
              only.
            </p>
          </template>
        </TestPriceAlert>
      </v-form>

      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        id="connexin_next_section"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FormRulesMixin from "../../mixins/FormRulesMixin";
import DropDownMixin from "../../mixins/DropDownMixin";
import { useSectionStore } from "../../store/SectionStore";
import TestPriceAlert from "../common/TestPriceAlert.vue";
import UploadComponent from "../upload/UploadComponent.vue";
import UploadMixin from "../../mixins/UploadMixin.vue";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import { EventBus } from "@/event-bus";

export default {
  name: "",
  mixins: [FormRulesMixin, DropDownMixin, UploadMixin, RetrieveJSONMixin],
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
  },
  components: {
    TestPriceAlert,
    UploadComponent,
  },
  watch: {
    price: function() {
      this.$emit("toggleTotalPrice", this.price, this.testId);
    },
    familyDOBVal: function() {
      this.familyMemberDOBDisplay = this.familyDOBVal
        ? this.formatDate(this.familyDOBVal)
        : "";
    },
  },
  data: () => ({
    connexinPricing: null,
    previouslyTested: "",
    relationAffected: "",
    rhipToPosCtrl: "",
    yesNoOptions: [
      { key: "Yes", value: true },
      { key: "No", value: false },
    ],
    posCtrlSampleId: "",
    clinicalNotes: "",
    testType: "",
    testTypeOptions: [
      { key: "Diagnostic", value: "diagnosticTesting" },
      { key: "Carrier", value: "carrierTesting" },
      { key: "Partner screening", value: "partnerScreening" },
    ],
    consanguinity: null,
    posCtrlName: "",
    familyMemberDOBMenu: false,
    familyDOBVal: null,
    familyMemberDOBDisplay: "",

    minDate: "1801-01-01",
    maxDate: new Date().toJSON().slice(0, 10),
  }),

  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.connexinPricing = await this.execRetrieveJson(
      "json/pricing/connexin_pricing.json"
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    const element = document.getElementById("connexin-test-title");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  },

  computed: {
    getPriceDetails() {
      if (!this.connexinPricing) return null;
      return this.connexinPricing[this.testType];
    },
    price() {
      if (
        useSectionStore().victorianPatient &&
        this.testType === "diagnosticTesting"
      ) {
        useSectionStore().freeTestForVictorian = true;
        return 0;
      } else {
        const priceDetails = this.getPriceDetails;
        return priceDetails ? priceDetails.price : null;
      }
    },
    testCode() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return this.testId + "_" + priceDetails.testCode;
      } else {
        return this.testId;
      }
    },
    medicare() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.medicare;
      } else {
        return null;
      }
    },
  },

  methods: {
    formatDate: function(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },
    reverseFormatDate: function(date) {
      if (!date) return null;
      const [day, month, year] = date.split("/");

      return `${year}-${month}-${day}`;
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    transferData: function() {
      return {
        testType: this.testId,
        familyPrevTested: this.previouslyTested,
        posCtrlSampleId: this.posCtrlSampleId,
        relationAffected: this.relationAffected,
        consanguinity: this.consanguinity,
        clinicalNotes: this.clinicalNotes,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        medicare: this.medicare,
        rhipToPosCtrl: this.rhipToPosCtrl,
        posCtrlName: this.posCtrlName,
        posCtrlBirthDate: this.reverseFormatDate(this.familyMemberDOBDisplay),
      };
    },
  },
};
</script>
